import React, { useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { styled } from '@mui/material';

import { Box, Stack, Tooltip, CompletionButton, Tag, IconButton, Tabs, Divider, Text } from 'shell/components';
import { useAuth } from 'contexts/auth';
import { useViewport } from 'contexts/viewport';
import { PageContext } from 'pages/PeerEvaluation/PageProvider';
import { getUserPreferredFullName } from 'utils/helper';
import {
    analyzeAlerts,
    RenderAlerts,
    ZeroSumMobileReceived,
    ZeroSumMobileGiven,
    RubricMobileGiven,
    RubricMobileReceived,
} from 'components/organisms/PeerEvaluation/PeerEvaluationComponents';

const ViewSubmissionsMobile = ({ students, submissionList = [], groups }) => {
    const { auth } = useAuth();
    const { device } = useViewport();
    const {
        currentPeerEvaluation,
        handleOpenDialog,

        // Constants
        DIALOG,
    } = useContext(PageContext);

    const [selectedStudent, setSelectedStudent] = useState('');
    const [defaultRoute, setDefaultRoute] = useState('received');

    const handleOpenSubmissionTable = (owner_account_id) => {
        if (selectedStudent === owner_account_id) {
            return setSelectedStudent('');
        }
        return setSelectedStudent(owner_account_id);
    };

    const handleStudentNotication = (owner_account_id) => {
        const findStudent = students.find((student) => student.owner_account_id === owner_account_id);
        return handleOpenDialog(DIALOG.PEER_EVALUATION_NOTIFICATIONS_TO_STUDENT, {
            id: currentPeerEvaluation.id,
            accounts: [findStudent],
        });
    };

    const props = {
        device,
        isInstructor: auth.role === 'instructor',
        currentPeerEvaluation,
        selectedStudent,
        students,
        submissionList,
    };

    const renderReceived = () => {
        return currentPeerEvaluation.format.value === 'rubric' ? (
            <RubricMobileReceived {...props} />
        ) : (
            <ZeroSumMobileReceived {...props} />
        );
    };

    const renderGiven = () => {
        return currentPeerEvaluation.format.value === 'rubric' ? (
            <RubricMobileGiven {...props} />
        ) : (
            <ZeroSumMobileGiven {...props} />
        );
    };

    const tabList = [
        {
            key: 'received',
            value: 'Received',
            content: renderReceived(),
        },
        {
            key: 'given',
            value: 'Given',
            content: renderGiven(),
        },
    ];

    const renderTabs = () => {
        return (
            <Tabs
                aria-label={'select rubric peer evaluation option to view submission'}
                options={tabList}
                selected={defaultRoute}
                onChange={setDefaultRoute}
            />
        );
    };

    return (
        <Stack>
            {students.length > 0 && <Divider />}
            <Stack divider={<Divider />}>
                {(students || [])
                    .filter((student) => student.group_id)
                    .map((student) => {
                        const findStudentSubmission = submissionList.find(
                            (submission) => submission.owner_account_id === student.owner_account_id
                        );
                        const findGroupSubmissions = submissionList.filter(
                            (submission) =>
                                submission.data?.[student.owner_account_id] && submission.group_id === student.group_id
                        );
                        const findGroup = groups.find((group) => group.id === student.group_id);
                        const findAlerts = analyzeAlerts(
                            student.owner_account_id,
                            findGroupSubmissions,
                            currentPeerEvaluation.format.value === 'rubric'
                        );
                        return (
                            <Stack key={student.owner_account_id}>
                                <MobileSubmissionStack
                                    spacing={3}
                                    onClick={(e) => {
                                        return handleOpenSubmissionTable(student.owner_account_id);
                                    }}
                                >
                                    <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                        <Text data-cy={'view-submission-student-name'} variant={'p1'}>
                                            {getUserPreferredFullName(student)}
                                        </Text>
                                        <Stack alignItems={'center'} direction={'row'} spacing={1}>
                                            {findAlerts?.length > 0 && (
                                                <Tooltip content={RenderAlerts(findAlerts)} type={'alert'} />
                                            )}
                                            {!findStudentSubmission && (
                                                <IconButton
                                                    aria-label={'notify the student'}
                                                    kind={'bell'}
                                                    size={'medium'}
                                                    variant={'square'}
                                                    onClick={() => handleStudentNotication(student.owner_account_id)}
                                                />
                                            )}
                                            <CompletionButton
                                                aria-label={findStudentSubmission ? 'completed' : 'not completed'}
                                                checked={findStudentSubmission}
                                            />
                                            {findStudentSubmission?.created_on > currentPeerEvaluation.due_on && (
                                                <Tag type={'submission'} value={'late'} />
                                            )}
                                        </Stack>
                                    </Stack>

                                    <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                        <Text variant={'p2'} color={'light'}>{`Team: ${findGroup.name}`}</Text>
                                    </Stack>

                                    <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                        <Text variant={'p2'} color={'light'}>
                                            {`Submitted On: ${
                                                findStudentSubmission
                                                    ? moment(findStudentSubmission.created_on).format(
                                                          'MMM D [at] hh:mm a'
                                                      )
                                                    : '--'
                                            }`}
                                        </Text>
                                    </Stack>
                                </MobileSubmissionStack>
                                {selectedStudent === student.owner_account_id && (
                                    <Stack
                                        key={student.owner_account_id}
                                        sx={{
                                            height: 'fit-content',
                                            width: '100%',
                                        }}
                                    >
                                        <SubmissionGradeBox>{renderTabs(student.owner_account_id)}</SubmissionGradeBox>
                                    </Stack>
                                )}
                            </Stack>
                        );
                    })}
            </Stack>
            {students.length > 0 && <Divider />}
        </Stack>
    );
};

const MobileSubmissionStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(5, 4),
    };
});

const SubmissionGradeBox = styled(Box)(({ theme }) => {
    return {
        width: '100%',
        height: '100%',
        border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        borderRadius: theme.spacing(1),
    };
});

export default ViewSubmissionsMobile;
